'use client'

import { useEffect, useRef } from 'react'
import { cn } from '@/utils/styles/cn'

/**
 * Superscale "agent mark" — a procedural, symmetric pixel-churn rendered to a <canvas>, ported from
 * the Brand Studio `superscale-agent.js`. Each preset is a distinct brand look (colour / density /
 * speed / symmetry / seed). A single shared requestAnimationFrame drives every mark on the page; marks
 * auto-pause when off-screen or when the tab is hidden, and render one static frame under
 * prefers-reduced-motion.
 */

const AGENT_MARK_PALETTE = {
    orange: '#FF5A0A',
    forest: '#1A250D',
    lime: '#CDF014',
    brown: '#453E38',
    yellow: '#FCEEAB',
    black: '#131210',
    peri: '#A6BFFA',
    cream: '#F2EFE9',
    cobalt: '#3D5BF5',
    aqua: '#3FD9CF',
} as const

interface IAgentMarkPresetConfig {
    res: number
    sym: 'mirror' | 'mandala'
    den: number
    speed: number
    jitter: number
    amp: number
    bspeed: number
    minAlpha: number
    fg: string
    bg: string
    seed: number
}

/** Procedural mark presets shared across marketing and authenticated agent surfaces. */
export const AGENT_MARK_PRESETS = {
    thinking: {
        res: 26,
        sym: 'mirror',
        den: 0.5,
        speed: 2.4,
        jitter: 0.25,
        amp: 0.12,
        bspeed: 0.6,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.orange,
        bg: AGENT_MARK_PALETTE.brown,
        seed: 1,
    },
    idle: {
        res: 20,
        sym: 'mirror',
        den: 0.32,
        speed: 0.9,
        jitter: 0.15,
        amp: 0.08,
        bspeed: 0.35,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.cream,
        bg: AGENT_MARK_PALETTE.black,
        seed: 7,
    },
    working: {
        res: 30,
        sym: 'mirror',
        den: 0.6,
        speed: 3.4,
        jitter: 0.35,
        amp: 0.1,
        bspeed: 0.8,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.lime,
        bg: AGENT_MARK_PALETTE.forest,
        seed: 3,
    },
    searching: {
        res: 28,
        sym: 'mandala',
        den: 0.48,
        speed: 2,
        jitter: 0.5,
        amp: 0.14,
        bspeed: 0.5,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.peri,
        bg: AGENT_MARK_PALETTE.black,
        seed: 5,
    },
    processing: {
        res: 34,
        sym: 'mandala',
        den: 0.55,
        speed: 2.8,
        jitter: 0.2,
        amp: 0.1,
        bspeed: 0.7,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.yellow,
        bg: AGENT_MARK_PALETTE.brown,
        seed: 9,
    },
    streaming: {
        res: 40,
        sym: 'mirror',
        den: 0.5,
        speed: 4.2,
        jitter: 0.4,
        amp: 0.06,
        bspeed: 1,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.orange,
        bg: AGENT_MARK_PALETTE.black,
        seed: 2,
    },
    waiting: {
        res: 22,
        sym: 'mirror',
        den: 0.4,
        speed: 1.2,
        jitter: 0.1,
        amp: 0.2,
        bspeed: 0.3,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.yellow,
        bg: AGENT_MARK_PALETTE.forest,
        seed: 4,
    },
    alert: {
        res: 26,
        sym: 'mandala',
        den: 0.62,
        speed: 4.6,
        jitter: 0.6,
        amp: 0.05,
        bspeed: 1.2,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.orange,
        bg: AGENT_MARK_PALETTE.forest,
        seed: 6,
    },
    focus: {
        res: 24,
        sym: 'mirror',
        den: 0.45,
        speed: 1.8,
        jitter: 0.2,
        amp: 0.1,
        bspeed: 0.5,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.black,
        bg: AGENT_MARK_PALETTE.cream,
        seed: 8,
    },
    dream: {
        res: 32,
        sym: 'mandala',
        den: 0.42,
        speed: 1.4,
        jitter: 0.3,
        amp: 0.18,
        bspeed: 0.4,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.peri,
        bg: AGENT_MARK_PALETTE.forest,
        seed: 11,
    },
    spark: {
        res: 36,
        sym: 'mandala',
        den: 0.38,
        speed: 3.6,
        jitter: 0.55,
        amp: 0.08,
        bspeed: 0.9,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.lime,
        bg: AGENT_MARK_PALETTE.black,
        seed: 12,
    },
    ember: {
        res: 28,
        sym: 'mirror',
        den: 0.5,
        speed: 2.2,
        jitter: 0.3,
        amp: 0.12,
        bspeed: 0.6,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.yellow,
        bg: AGENT_MARK_PALETTE.black,
        seed: 13,
    },
    scan: {
        res: 30,
        sym: 'mandala',
        den: 0.5,
        speed: 2.6,
        jitter: 0.35,
        amp: 0.1,
        bspeed: 0.7,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.brown,
        bg: AGENT_MARK_PALETTE.yellow,
        seed: 14,
    },
    pulse: {
        res: 28,
        sym: 'mirror',
        den: 0.5,
        speed: 2.3,
        jitter: 0.3,
        amp: 0.12,
        bspeed: 0.6,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.cobalt,
        bg: AGENT_MARK_PALETTE.black,
        seed: 15,
    },
    flux: {
        res: 30,
        sym: 'mandala',
        den: 0.48,
        speed: 2.7,
        jitter: 0.35,
        amp: 0.1,
        bspeed: 0.7,
        minAlpha: 0,
        fg: AGENT_MARK_PALETTE.aqua,
        bg: AGENT_MARK_PALETTE.forest,
        seed: 16,
    },
} satisfies Record<string, IAgentMarkPresetConfig>

/** Name of one procedural agent-mark preset. */
export type IAgentMarkPreset = keyof typeof AGENT_MARK_PRESETS

interface IAgentMarkField {
    ph: number[][]
    jit: number[][]
}

// deterministic PRNG (mulberry32) — identical to the Brand Studio source
function mulberry(seed: number) {
    let a = seed
    return () => {
        a |= 0
        a = (a + 0x6d2b79f5) | 0
        let t = Math.imul(a ^ (a >>> 15), 1 | a)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}
const ease = (t: number) => {
    t = Math.max(0, Math.min(1, t))
    return t * t * (3 - 2 * t)
}

// A symmetric grid of per-cell values (mirror or mandala orbits), so the churn reads as a living tile.
function symBuild(N: number, sym: string, seed: number, scale: number): number[][] {
    const r = mulberry(seed)
    const g: number[][] = []
    const h = Math.ceil(N / 2)
    for (let y = 0; y < N; y++) g.push(new Array<number>(N).fill(0))
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < h; x++) {
            if (sym === 'mandala' && x < y) continue
            const v = r() * scale
            const xs = [x, N - 1 - x]
            const ys = [y, N - 1 - y]
            for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) g[ys[i]][xs[j]] = v
            if (sym === 'mandala') {
                const xs2 = [y, N - 1 - y]
                const ys2 = [x, N - 1 - x]
                for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) g[ys2[i]][xs2[j]] = v
            }
        }
    }
    return g
}
function buildField(N: number, sym: string, seed: number): IAgentMarkField {
    return { ph: symBuild(N, sym, seed, Math.PI * 2), jit: symBuild(N, sym, seed + 991, 1) }
}

function drawChurn(
    ctx: CanvasRenderingContext2D,
    size: number,
    N: number,
    P: IAgentMarkPresetConfig,
    F: IAgentMarkField,
    t: number
) {
    const cl = size / N
    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = P.bg
    ctx.fillRect(0, 0, size, size)
    ctx.fillStyle = P.fg
    const thr = 1 - P.den + P.amp * Math.sin(t * P.bspeed)
    for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
            const a = 0.5 + 0.5 * Math.sin(t * P.speed * (1 + (F.jit[y][x] - 0.5) * P.jitter) + F.ph[y][x])
            if (a <= thr) continue
            const it = ease((a - thr) / (1 - thr || 1e-6))
            ctx.globalAlpha = P.minAlpha + (1 - P.minAlpha) * it
            ctx.fillRect(x * cl, y * cl, cl + 0.6, cl + 0.6)
        }
    }
    ctx.globalAlpha = 1
}

// One shared rAF drives every mounted mark, so a wall of marks stays cheap.
interface IAgentMarkInstance {
    active: boolean
    visible: boolean
    clock: number
    render: () => void
}

const instances: IAgentMarkInstance[] = []
let running = false
let tickLast = 0
function tick(now: number) {
    const dt = tickLast ? Math.min(0.1, (now - tickLast) / 1000) : 0
    tickLast = now
    const hidden = typeof document !== 'undefined' && document.hidden
    for (const inst of instances) {
        if (!inst.active || !inst.visible || hidden) continue
        inst.clock += dt
        inst.render()
    }
    if (instances.length) requestAnimationFrame(tick)
    else running = false
}
function ensureRunning() {
    if (running || !instances.length) return
    running = true
    tickLast = 0
    requestAnimationFrame(tick)
}

interface ISuperscaleAgentMarkProps {
    preset: IAgentMarkPreset
    className?: string
    /** Whether the procedural mark should advance; inactive marks return to their first frame. */
    isActive?: boolean
}

/** Renders the shared animated Superscale employee identity mark. */
export function SuperscaleAgentMark({ preset, className, isActive = true }: ISuperscaleAgentMarkProps) {
    const hostRef = useRef<HTMLSpanElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const instanceRef = useRef<IAgentMarkInstance | null>(null)
    const isActiveRef = useRef(isActive)
    isActiveRef.current = isActive

    useEffect(() => {
        const host = hostRef.current
        const canvas = canvasRef.current
        if (!host || !canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const P = AGENT_MARK_PRESETS[preset]
        const field = buildField(P.res, P.sym, P.seed)
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        let css = 0
        const resize = () => {
            // Rasterize ABOVE the display size: the hosts are tiny (36–52px), so the ~1.5px churn
            // cells land on fractional coordinates and get smeared by canvas AA — and the vision
            // globe additionally scales marks up via transform, which stretches this backing store.
            // 2x overscan keeps the churn crisp in both cases at negligible texture cost.
            const dpr = Math.min((window.devicePixelRatio || 1) * 2, 4)
            css = host.clientWidth || 40
            canvas.width = css * dpr
            canvas.height = css * dpr
            canvas.style.width = `${css}px`
            canvas.style.height = `${css}px`
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            drawChurn(ctx, css, P.res, P, field, instance.clock)
        }
        const instance: IAgentMarkInstance = {
            active: isActiveRef.current,
            visible: true,
            clock: reduce ? 2 : 0,
            render: () => drawChurn(ctx, css, P.res, P, field, instance.clock),
        }
        instanceRef.current = instance

        const resizeObserver = new ResizeObserver(resize)
        resizeObserver.observe(host)
        resize()

        if (reduce) {
            return () => resizeObserver.disconnect()
        }

        const intersection = new IntersectionObserver(
            ([entry]) => {
                instance.visible = entry.isIntersecting
                if (instance.visible) instance.render()
            },
            { threshold: 0.01 }
        )
        intersection.observe(host)

        instances.push(instance)
        ensureRunning()

        return () => {
            resizeObserver.disconnect()
            intersection.disconnect()
            const index = instances.indexOf(instance)
            if (index >= 0) instances.splice(index, 1)
            instanceRef.current = null
        }
    }, [preset]) // `isActive` updates the live instance below without resetting its frame.

    useEffect(() => {
        const instance = instanceRef.current
        if (!instance) return

        instance.active = isActive
        if (isActive) {
            instance.render()
            ensureRunning()
        } else {
            instance.clock = 0
            instance.render()
        }
    }, [isActive])

    return (
        <span
            ref={hostRef}
            data-agent-mark={preset}
            data-agent-mark-active={isActive}
            className={cn('block overflow-hidden rounded-full', className)}
            aria-hidden
        >
            <canvas ref={canvasRef} className="block size-full" />
        </span>
    )
}
